import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { frenchTypography } from '@/lib/utils';
import { occScoreLookup, wordTint, normWordKey, occKey } from '../lib/score';

/**
 * Shared phrase display for practice sessions (Manual + Automatic) so the
 * layout stays consistent across every mode. Order: English translation on
 * top, the French practice text below (with karaoke word highlighting), and
 * phonetics hidden behind a show/hide toggle (revealed on demand).
 *
 * Props:
 *  - phrase: { french, english, phonetic }
 *  - activeCharIndex / karaokeEnabled: drive the French word highlight
 *  - englishSpeaking: highlight the English line while it's being spoken (Automatic)
 *  - wordScores: optional [{word, score}] from Speechace — when supplied,
 *    words are tinted here so the score view doesn't need to repeat the phrase
 *    in a separate coloured block. Ignored while karaoke is actively
 *    highlighting a word (playback trumps the score tint).
 *  - tipKeys: the words that have feedback underneath (lib/score feedbackKeys).
 *    ONLY those are tinted: a colour with no card to explain it is a puzzle,
 *    whether the word scored 30 or 97.
 *  - onPickWord: optional ({word, occ}) => void. When given, the highlighted
 *    words become buttons; tapping one hands it back. Nothing else changes.
 *  - pickedKey: occKey of the picked word, ringed so it is clear which card
 *    is showing.
 *  - className: extra classes on the root (e.g. bottom margin)
 *  - frenchTestId / englishTestId: optional data-testids for the two lines
 */
export default function PhraseDisplay({
    phrase,
    activeCharIndex = -1,
    karaokeEnabled = true,
    englishSpeaking = false,
    wordScores = null,
    tipKeys = null,
    onPickWord = null,
    pickedKey = null,
    className = '',
    frenchTestId,
    englishTestId,
    // Shown straight under the French, above the phonetics toggle: Automatic
    // puts the word tips here so they sit right under the phrase (Leanne,
    // 4 Nov 2026).
    belowPhrase = null,
}) {
    const [showPhonetic, setShowPhonetic] = useState(false);
    if (!phrase) return null;

    // Per-word score tint, looked up by word AND which time it comes up. It
    // used to give every "le" in a phrase the lowest score of them all, so a
    // well-said one was painted red and, worse, a word could look flagged twice
    // with only one tip card to answer it. Shared with the tips list so the two
    // cannot drift apart again.
    const scores = occScoreLookup(wordScores);
    const nextOccurrence = scores.counter();

    // Always render word spans so layout never shifts when highlighting starts.
    const text = frenchTypography(phrase.french || '');
    const wordRegex = /\S+/g;
    // French typography puts a space before ? ! ; : and », so those land as
    // tokens of their own. They are punctuation, not words: no padding, no
    // karaoke highlight, no score tint.
    const hasLetter = /[\p{L}\p{M}]/u;
    const segments = [];
    let lastEnd = 0;
    let match;
    while ((match = wordRegex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        if (start > lastEnd) segments.push({ text: text.slice(lastEnd, start), isWord: false, active: false, scoreBg: null });
        if (!hasLetter.test(match[0])) {
            segments.push({ text: match[0], isWord: false, active: false, scoreBg: null });
            lastEnd = end;
            continue;
        }
        const isActive = karaokeEnabled && activeCharIndex >= 0 && activeCharIndex >= start && activeCharIndex < end;
        // Counted for every word, tinted or not, so the tally stays in step
        // with the phrase however the scores came back.
        const occ = nextOccurrence(match[0]);
        // Playback highlight (karaoke) wins over the score tint so the wave of
        // colour during listening is never obscured.
        // Tinted only when this attempt owns a tip card, so every colour on
        // the phrase has an explanation underneath it.
        const hasTip = !tipKeys || tipKeys.has(`${normWordKey(match[0])}#${occ}`);
        const scoreBg = isActive || !scores.size || !hasTip
            ? null : wordTint(scores.get(match[0], occ));
        const clean = match[0].replace(/[^\p{L}\p{M}'\-]/gu, '');
        segments.push({ text: match[0], isWord: true, active: isActive, scoreBg, word: clean, occ });
        lastEnd = end;
    }
    if (lastEnd < text.length) segments.push({ text: text.slice(lastEnd), isWord: false, active: false, scoreBg: null });

    return (
        <div className={`text-center ${className}`}>
            {/* English translation — on top */}
            {phrase.english && (
                <p className="text-base text-stone-500 mb-3" data-testid={englishTestId}>
                    <span className={englishSpeaking ? 'px-1 rounded animate-highlight' : ''}>
                        {phrase.english}
                    </span>
                </p>
            )}

            {/* French practice text */}
            <p className="text-xl md:text-2xl font-bold text-foreground leading-snug" data-testid={frenchTestId}>
                {/* Every word can be tapped to hear it and see its card, the
                    same as Manual (Leanne, 18 Oct 2026), not only the tinted ones. */}
                {segments.map((seg, i) => (onPickWord && seg.isWord && seg.word) ? (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onPickWord({ word: seg.word, occ: seg.occ })}
                        className={`px-1 rounded ${seg.scoreBg || 'hover:bg-primary/10'} ${
                            pickedKey === occKey(seg.word, seg.occ) ? 'relative z-10 ring-2 ring-primary ring-offset-1' : ''}`}
                    >
                        {seg.text}
                    </button>
                ) : (
                    <span
                        key={i}
                        className={seg.isWord
                            ? `px-1 rounded transition-colors duration-75${seg.active ? ' bg-amber-600/30' : seg.scoreBg ? ` ${seg.scoreBg}` : ''}`
                            : ''}
                    >
                        {seg.text}
                    </span>
                ))}
            </p>

            {belowPhrase}

            {/* Phonetics — hidden by default, revealed with the toggle */}
            {phrase.phonetic && (
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={() => setShowPhonetic(v => !v)}
                        className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 transition-colors"
                        data-testid="phonetic-toggle"
                        aria-expanded={showPhonetic}
                    >
                        {showPhonetic ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {showPhonetic ? 'Hide phonetics' : 'Show phonetics'}
                    </button>
                    {showPhonetic && (
                        // Strip any brackets the phonetic was stored with (older IPA
                        // ones were generated wrapped) so it never renders as [[..]].
                        <p className="text-sm font-mono text-stone-400 mt-1" data-testid="phonetic-text">[{phrase.phonetic.replace(/^\[|\]$/g, '')}]</p>
                    )}
                </div>
            )}
        </div>
    );
}
